import { NgFor, NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAKE_ICON } from '../../../../app.component';
import { FormEntryService } from '../../../../services/form-entry.service';
import { MakeService } from '../../../../services/make.service';
import { AiConfigurationPageEventsService } from '../../../../events/ai-configuration-page-events.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AiAgentResponse } from '../../../../models/responses/ai-agent-response';
import { MakeResponse } from '../../../../models/responses/make-response';
import { FIELD_DESCRIPTION_VALIDATOR, FIELD_KEY_VALIDATOR, FIELD_NAME_VALIDATOR } from '../zapier-integration-dialog/zapier-integration-dialog.component';
import { FormEntryResponse } from '../../../../models/responses/form-entry-response';
import { ZapResponse } from '../../../../models/responses/zap-response';
import { FormEntriesComponent } from '../form-entries/form-entries.component';

@Component({
  selector: 'app-make-integration-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatDivider,
    MatIconModule,
    NgIf,
    NgFor,
    MatProgressSpinnerModule,
    FormEntriesComponent
  ],
  templateUrl: './make-integration-dialog.component.html',
  styleUrl: './make-integration-dialog.component.scss'
})
export class MakeIntegrationDialogComponent {
  makeForm!: FormGroup;
  isLoading: boolean = false;
  makeIcon: string = MAKE_ICON;
  isMakeTestValid: boolean = true;
  isMakeSaveLoading: boolean = false;
  isMakeTestLoading: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private formEntryService: FormEntryService,
    private makeService: MakeService,
    private aiConfigurationPageEventService: AiConfigurationPageEventsService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<MakeIntegrationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { aiAgent: AiAgentResponse, make: MakeResponse },
  ) { }

  ngOnInit() {
    this.initMakeForm();
  }

  initMakeForm() {
    this.makeForm = this.formBuilder.group({
      name: [this.data.make ? this.data.make.name : '', Validators.required],
      makeId: [this.data.make ? this.data.make.makeId : '', Validators.required],
      fields: this.formBuilder.array([])
    });

  }

  onAddField() {
    this.fields.push(this.formBuilder.group({
      id: [''],
      fieldName: ['', FIELD_NAME_VALIDATOR],
      fieldDescription: ['', FIELD_DESCRIPTION_VALIDATOR],
      fieldKey: ['', FIELD_KEY_VALIDATOR]
    }));
  }

  onRemoveField(index: number) {
    this.fields.removeAt(index);
  }

  onSubmit() {
    if (this.makeForm.valid) {
      this.isLoading = true;
      this.isMakeSaveLoading = true;
      const formValues = this.makeForm.value;

      if (this.data.make)
        this.updateMake(formValues);
      else
        this.createMake(formValues);
    }
  }
  onTestMake() {
    if (this.makeForm.valid) {
      this.isLoading = true;
      this.isMakeTestLoading = true;
      const makeId = this.makeForm.value['makeId'];
      const jsonObject: { [key: string]: string } = {};
      
      this.getFieldsValue().forEach(formEntryResponse => {
        jsonObject[formEntryResponse.fieldKey] = 'dummyData';
      });
      
      this.makeService.test(makeId, jsonObject).subscribe({
        next: () => {
          this.isLoading = false;
          this.isMakeTestLoading = false;
          this.isMakeTestValid = true;
          this.openSnackbar('Make test was successful');
        },
        error: () => {
          this.isLoading = false;
          this.isMakeTestLoading = false;
          this.isMakeTestValid = false;
          this.openSnackbar('Make test was not successful');
        }
      });
    }
  }

  private createMake(formValues: any) {
    const make: MakeResponse = {
      name: formValues['name'],
      makeId: formValues['makeId'],
      aiAgentId: this.data.aiAgent.id,
      formEntries: this.getFieldsValue()
    };

    this.makeService.createMake(make).subscribe({
      next: (make) => {
        this.isLoading = false;
        this.isMakeSaveLoading = false;
        this.aiConfigurationPageEventService.onMakeIntegrationAddedEvent.emit(make);
        this.dialogRef.close();
        this.openSnackbar('Make created successfully');
      },
      error: () => {
        alert('Server Error, contact support');
        this.isLoading = false;
        this.isMakeSaveLoading = false;
      }
    });
  }

  private updateMake(formValues: any) {
    const make: MakeResponse = {
      id: this.data.make.id,
      name: formValues['name'],
      makeId: formValues['makeId'],
      aiAgentId: this.data.aiAgent.id,
      formEntries: this.getFieldsValue()
    };



    this.makeService.updateZap(make).subscribe({
      next: () => {
        this.deleteFormEntries(make);
        this.updateFormEntries(make);
        this.aiConfigurationPageEventService.onRefreshAiAgentEvent.emit();
      },
      error: () => {
        alert('Server Error, contact support');
        this.isLoading = false;
      }
    });
  }

  private deleteFormEntries(make: MakeResponse) {
    const formEntriesToDelete: (number)[] = this.data.make.formEntries
      .filter(formEntry => !make.formEntries.some(fe => fe.id == formEntry.id))
      .map(fe => fe.id!);

    if (formEntriesToDelete.length > 0) {
      this.formEntryService.deleteEntries(formEntriesToDelete).subscribe({
        next: () => {
        },
        error: () => {
          alert('Server error, contact support');
        }
      });
    }
  }

  private updateFormEntries(make: MakeResponse) {
    this.formEntryService.upsertBatchEntries(make.formEntries, this.data.aiAgent.id).subscribe({
      next: () => {
        this.isLoading = false;
        this.isMakeSaveLoading = false;
        this.openSnackbar('Make updated successfully');
        this.dialogRef.close();
        this.aiConfigurationPageEventService.onRefreshAiAgentEvent.emit();
      },
      error: () => {
        alert('Server Error, contact support');
        this.isLoading = false;
        this.isMakeSaveLoading = false;
      }
    });
  }

  openSnackbar(message: string) {
    this.snackBar.open(message, 'Close', { duration: 5000 });
  }

  getFieldsValue(): FormEntryResponse[] {
    const values: FormEntryResponse[] = [];

    for (let index = 0; index < this.fields.length; index++) {
      const formGroupValue = this.fields.controls[index].value;
      const fieldId: number = parseInt(formGroupValue['id']);

      if (this.data.make) {
        values.push({
          id: isNaN(fieldId) ? undefined : fieldId,
          fieldName: formGroupValue['fieldName'],
          fieldDescription: formGroupValue['fieldDescription'],
          fieldType:formGroupValue['fieldType'],
          fieldKey: formGroupValue['fieldKey'],
          makeId: this.data.make.id
        });
      } else {
        values.push({
          id: isNaN(fieldId) ? undefined : fieldId,
          fieldName: formGroupValue['fieldName'],
          fieldDescription: formGroupValue['fieldDescription'],
          fieldType:formGroupValue['fieldType'],
          fieldKey: formGroupValue['fieldKey']
        });
      }
    }

    return values;
  }
  get fields(): FormArray<FormGroup> {
    return this.makeForm.controls['fields'] as FormArray<FormGroup>;
  }


}
